import React, { useMemo } from "react";
import { useRouteMatch } from "react-router";
import { InputLabel } from "@material-ui/core";
import { KDatePicker, KButton, KSearchInput, KDialog, KTextInput, KIcon } from "@kyburz/kdesign-system";
import useSafeSetState from "../../../../hooks/useSafeSetState";
import { getLogsByDevice, createLogEntry } from "../../../../apiCalls/config/devices";
import { formatUtcDate } from "../../../../utils/timeUtils";
import KInfiniteTable from "../../../../components/table/KInfiniteTable";
import {useStyles} from "./styles";

interface LogEntry {
  id: number;
  created_at: string;
  created_by: string;
  content: string;
}

const today = new Date();
const tomorrow = new Date();
tomorrow.setDate(today.getDate() + 1);

const DeviceLogs = () => {
  const match = useRouteMatch({
    path: "/config/devices/device/:imei/logs",
    strict: true,
    sensitive: true,
  });
  const { imei } = match?.params as any;
  const classes = useStyles();

  const [
    { startDate, endDate, startTime, endTime, searchQuery, logs, loading, openDialog, logContent, createdBy, createLoading, detailLog },
    setState,
  ] = useSafeSetState({
    startDate: today,
    endDate: tomorrow,
    startTime: "00:00",
    endTime: "00:00",
    searchQuery: "",
    logs: [] as LogEntry[],
    loading: false,
    openDialog: false,
    logContent: "",
    createdBy: "",
    createLoading: false,
    detailLog: null as LogEntry | null,
  });

  const onDateChange = (dates): void => {
    const [start, end] = dates;
    setState({ startDate: start, endDate: end });
  };

  const handleTimeChange = (e): void => {
    setState({ [e.target.name]: e.target.value });
  };

  const handleGetLogs = (query: string = searchQuery) => {
    setState({ loading: true });
    getLogsByDevice(
      imei,
      startDate.toISOString().slice(0, 10),
      endDate.toISOString().slice(0, 10),
      startTime,
      endTime,
      query
    ).then((res) => {
      if (res && res.data) {
        const data = res.data.data
          // sort by event date from latest to earliest
          .sort((a, b) => (b?.id || 0) - (a?.id || 0))
          .map((log) => ({
            id: log?.id,
            created_at: formatUtcDate(log?.created_at),
            created_by: log?.created_by || "",
            content: log?.content || "",
          }));
        setState({ logs: data, loading: false });
      } else {
        setState({ logs: [], loading: false });
      }
    });
  };

  const handleOpenDialog = () => {
    setState({ openDialog: true, logContent: "", createdBy: "" });
  };

  const handleCloseDialog = () => {
    setState({ openDialog: false, logContent: "", createdBy: "" });
  };

  const handleCreateLog = () => {
    if (!logContent.trim()) {
      return;
    }
    setState({ createLoading: true });
    createLogEntry(imei, logContent, createdBy).then((res) => {
      setState({ createLoading: false });
      if (res && res.data) {
        handleCloseDialog();
        // Refresh the logs after creating a new entry
        handleGetLogs();
      }
    });
  };

  const handleOpenDetail = (log: LogEntry) => {
    setState({ detailLog: log });
  };

  const handleCloseDetail = () => {
    setState({ detailLog: null });
  };

  const columns = useMemo(
    () => [
      {
        Header: "Date",
        accessor: "created_at",
        width: 200,
      },
      {
        Header: "User",
        accessor: "created_by",
        width: 220,
      },
      {
        Header: "Payload",
        accessor: "content",
        width: 600,
      },
      {
        Header: "",
        id: "actions",
        width: 70,
        disableSortBy: true,
        Cell: (row) => (
          <div
            style={{ textAlign: "center", cursor: "pointer" }}
            onClick={() => handleOpenDetail(row.row.original)}
          >
            <KIcon icon="eye" color="#383F51" size={20} />
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className={classes.root}>
      <div className={classes.controls}>
        <KDatePicker
          selected={startDate}
          onChange={onDateChange}
          selectsStart={true}
          label="Date Range"
          startDate={startDate}
          compact
          endDate={endDate}
        />
        <div className={classes.time}>
          <InputLabel classes={{ root: classes.title }}>From (UTC)</InputLabel>
          <input
            type="time"
            id="startTime"
            name="startTime"
            value={startTime}
            onChange={handleTimeChange}
            className={classes.timeInput}
          />
        </div>
        <div className={classes.time}>
          <InputLabel classes={{ root: classes.title }}>To (UTC)</InputLabel>
          <input
            type="time"
            id="endTime"
            name="endTime"
            value={endTime}
            onChange={handleTimeChange}
            className={classes.timeInput}
          />
        </div>
        <div className={classes.time}>
          <KSearchInput
            placeholder="Filter content"
            value={searchQuery}
            onChange={(e) => setState({ searchQuery: e.target.value })}
            onEndIconClick={() => {
              // Clear the filter and immediately reload the unfiltered list.
              // The empty query is passed explicitly because setState is async
              // and handleGetLogs would otherwise still see the old searchQuery.
              setState({ searchQuery: "" });
              handleGetLogs("");
            }}
          />
        </div>
        <KButton
          children="Search Logs"
          onClick={() => handleGetLogs()}
          loading={loading}
          disabled={loading}
          style={{ marginLeft: 16 }}
        />
        <KButton
          children="Create Log Entry"
          onClick={handleOpenDialog}
          style={{ marginLeft: 16 }}
        />
      </div>
      <div className={classes.tableContainer}>
        {logs.length > 0 ? (
          <KInfiniteTable
            columns={columns}
            data={logs}
            hasNextPage={false}
            isNextPageLoading={false}
            loading={loading}
            rootClassName={classes.tableRoot}
          />
        ) : (
          "No data for the selected time period"
        )}
      </div>

      <KDialog
        size="sm"
        title="Create Log Entry"
        open={openDialog}
        onClose={handleCloseDialog}
        secondaryCTAColor="secondary"
        secondaryCTAonClick={handleCloseDialog}
        secondaryCTAText="Cancel"
        primaryCTAText="Create"
        primaryCTAColor="primary"
        primaryCTAonClick={handleCreateLog}
        primaryCTALoading={createLoading}
        primaryCTADisabled={createLoading || !logContent.trim()}
      >
        <KTextInput
          name="createdBy"
          label="Created By"
          value={createdBy}
          onChange={(e) => setState({ createdBy: e.target.value })}
          placeholder="Enter creator name..."
          style={{ marginBottom: "16px" }}
        />
        <KTextInput
          name="logContent"
          label="Log Content"
          value={logContent}
          onChange={(e) => setState({ logContent: e.target.value })}
          multiline
          rows={4}
          placeholder="Enter log content..."
        />
      </KDialog>

      <KDialog
        size="sm"
        title="Log Entry"
        open={Boolean(detailLog)}
        onClose={handleCloseDetail}
        secondaryCTAColor="secondary"
        secondaryCTAonClick={handleCloseDetail}
        secondaryCTAText="Close"
      >
        {detailLog && (
          <div className={classes.detail}>
            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>Date</span>
              <span>{detailLog.created_at}</span>
            </div>
            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>User</span>
              <span>{detailLog.created_by || "-"}</span>
            </div>
            <div className={classes.detailRow}>
              <span className={classes.detailLabel}>Payload</span>
              <span>{detailLog.content || "-"}</span>
            </div>
          </div>
        )}
      </KDialog>
    </div>
  );
};

export default DeviceLogs;

